import { useState } from "react";
import { Eye, EyeOff } from 'lucide-react';
import { API_BASE_URL } from "../lib/authFetch";

interface ForgotPasswordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ForgotPasswordModal({ open, onOpenChange }: ForgotPasswordModalProps) {
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSendCode = async () => {
    setIsLoading(true);
    setError("");
    setSuccessMsg("");
    try {
      const res = await fetch(`${API_BASE_URL}/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setStep("code");
        setSuccessMsg("Código enviado para seu e-mail!");
      } else {
        const data = await res.json();
        setError(data.detail || "Erro ao enviar código");
      }
    } catch (err) {
      setError("Erro de conexão com o servidor");
    }
    setIsLoading(false);
  };

  const handleResetPassword = async () => {
    setIsLoading(true);
    setError("");
    setSuccessMsg("");
    try {
      const res = await fetch(`${API_BASE_URL}/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code, new_password: newPassword }),
      });
      if (res.ok) {
        setSuccessMsg("Senha redefinida com sucesso! Faça login com a nova senha.");
        setTimeout(() => {
          onOpenChange(false);
        }, 2000);
      } else {
        const data = await res.json();
        setError(data.detail || "Erro ao redefinir senha");
      }
    } catch (err) {
      setError("Erro de conexão com o servidor");
    }
    setIsLoading(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
      <div className="bg-protec-dark-light p-8 rounded-lg shadow-lg w-full max-w-md flex flex-col items-center">
        <h2 className="text-xl font-bold mb-2 text-protec-gold">Recuperar Senha</h2>
        {step === "email" && (
          <>
            <p className="text-gray-300 mb-4 text-center">Digite seu e-mail cadastrado para receber um código de recuperação.</p>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Seu e-mail"
              className="mb-2 px-4 py-2 rounded border border-protec-gold/30 bg-protec-dark text-white w-full text-center"
            />
            <button
              onClick={handleSendCode}
              disabled={isLoading || !email}
              className="w-full bg-gradient-gold text-black font-semibold py-2 rounded mb-2"
            >
              {isLoading ? "Enviando..." : "Enviar código"}
            </button>
          </>
        )}
        {step === "code" && (
          <>
            <p className="text-gray-300 mb-4 text-center">Digite o código recebido no e-mail e sua nova senha.</p>
            <input
              type="text"
              value={code}
              onChange={e => setCode(e.target.value)}
              placeholder="Código de 6 dígitos"
              className="mb-2 px-4 py-2 rounded border border-protec-gold/30 bg-protec-dark text-white w-full text-center"
            />
            <div className="relative w-full mb-2">
            <input
                type={showPassword ? "text" : "password"}
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="Nova senha"
                className="px-4 py-2 rounded border border-protec-gold/30 bg-protec-dark text-white w-full text-center pr-10"
            />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-protec-gold transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            <button
              onClick={handleResetPassword}
              disabled={isLoading || !code || !newPassword}
              className="w-full bg-gradient-gold text-black font-semibold py-2 rounded mb-2"
            >
              {isLoading ? "Redefinindo..." : "Redefinir senha"}
            </button>
          </>
        )}
        {successMsg && <div className="text-green-400 text-sm mb-2">{successMsg}</div>}
        {error && <div className="text-red-400 text-sm mb-2">{error}</div>}
        <button onClick={() => onOpenChange(false)} className="text-protec-gold text-xs underline mt-2">Cancelar</button>
      </div>
    </div>
  );
} 